/**
 * 分拣码段数推导与自定义规则求码（纯函数，转写自原型 codesAt / ruleCodes / customCodes 等）。
 *
 * 分拣码四段：Hub-配送仓-路区-编号，如 LA-SAN-A-3（原始码 LA-SAN-A3 中路区字母与编号拆为两段）。
 * 不分目的地是 Hub（如 EWR）还是配送仓（如 LA-SAN），可选段数统一为 2-4 段，默认 3 段
 * （与创建包标签页面一致：没有"1 段"这个选项，1 段意味着不按分拣码细分，不创建）。
 */

/** 规则中的「全部」占位 */
export const ALL_TOKEN = '*';

export interface CodeRule {
    /** 段数 */
    seg: number;
    /** 配送仓（仅 Hub 目的地可选）；[ALL_TOKEN] 表示全部 */
    warehouses: string[];
    /** 路区（三段码，如 LA-SAN-A）；[ALL_TOKEN] 表示全部 */
    routes: string[];
    /** 编号（四段码）；[ALL_TOKEN] 表示全部 */
    nums: string[];
    /** 每码包牌数 */
    copies: number;
}

export interface BagPlan {
    custom: boolean;
    seg?: number;
    codes: string[];
    copies: number;
    /** 合计包牌数 */
    total: number;
}

interface ScopeCode {
    hub: string;
    whs: string;
    route: string;
    num: string;
}

export const isHubDestination = (destination: string): boolean => !String(destination || '').includes('-');

/**
 * 目的地只到 Hub 本身时多一个「1 段」选项（codesAt 对 seg<=1 已经原生支持，返回 Hub 自己的码，
 * 如 "EWR"）；目的地精确到配送仓时不提供——包牌只印到 Hub 级分拣码物理上没法路由到具体配送仓，
 * 没有实际意义。
 */
export const segOptionsFor = (destination: string): number[] => (isHubDestination(destination) ? [1, 2, 3, 4] : [2, 3, 4]);

export const defaultSegFor = (_destination: string): number => 3;

const naturalCompare = (a: string, b: string): number => a.localeCompare(b, undefined, { numeric: true });

const uniqSorted = (arr: string[]): string[] => Array.from(new Set(arr)).sort(naturalCompare);

/**
 * 目的地范围内的原始分拣码拆解（按 Hub/Whs 前缀过滤，行为不变——`MoveInGroupCard.tsx` 等调用方
 * 目前传入的是跨目的地共享的 mock 码池，仍然依赖这层过滤才能筛出当前目的地的码；
 * `CreateBagsDrawer.tsx` 走的真实后端池子本身已经是按批次精确 scope 过的，过滤在那条链路上是
 * 无副作用的 no-op）。
 * 裸配送仓码（无路区，如新配送仓尚未配置 Area 时只有 "EWR-XYZ"）也保留——只贡献 2 段，
 * 3/4 段分组时在 `codesAt` 里再过滤掉
 */
const scopeCodes = (destination: string, allCodes: string[]): ScopeCode[] => {
    const parts = String(destination || '').split('-');
    const hit = isHubDestination(destination)
        ? allCodes.filter((c) => c.split('-')[0] === parts[0])
        : allCodes.filter((c) => {
            const t = c.split('-');
            return t[0] === parts[0] && t[1] === parts[1];
        });
    return hit
        .map((c) => {
            const t = c.split('-');
            const m = String(t[2] || '').match(/^([A-Za-z]+)(\d*)$/);
            return { hub: t[0], whs: t[1], route: m ? m[1] : String(t[2] || ''), num: m ? m[2] : '' };
        })
        .filter((x) => x.whs);
};

/** 目的地在指定段数下的分拣码集合（去重、自然序） */
export const codesAt = (destination: string, seg: number, allCodes: string[]): string[] => {
    const hub = String(destination || '').split('-')[0];
    const rows = scopeCodes(destination, allCodes);
    if (!rows.length) return [destination];
    if (seg <= 1) return isHubDestination(destination) ? [hub] : [destination];
    if (seg === 2) return uniqSorted(rows.map((x) => `${x.hub}-${x.whs}`));
    const withRoute = rows.filter((x) => x.route);
    if (!withRoute.length) return [destination];
    if (seg === 3) return uniqSorted(withRoute.map((x) => `${x.hub}-${x.whs}-${x.route}`));
    return uniqSorted(withRoute.map((x) => `${x.hub}-${x.whs}-${x.route}${x.num ? `-${x.num}` : ''}`));
};

/** 段选项按钮文案参数：单码时不带「所有」 */
export const segOptionIsSingle = (destination: string, seg: number, allCodes: string[]): boolean =>
    codesAt(destination, seg, allCodes).length === 1;

export const defaultRule = (destination: string): CodeRule => ({
    seg: defaultSegFor(destination),
    warehouses: [ALL_TOKEN],
    routes: [ALL_TOKEN],
    nums: [ALL_TOKEN],
    copies: 1,
});

const isAll = (values: string[]): boolean => values[0] === ALL_TOKEN;

/** 单条自定义规则命中的分拣码 */
export const ruleCodes = (destination: string, rule: CodeRule, allCodes: string[]): string[] => {
    const allW = isAll(rule.warehouses);
    const allR = isAll(rule.routes);
    const allN = isAll(rule.nums);
    return codesAt(destination, rule.seg, allCodes).filter((c) => {
        const t = c.split('-');
        if (rule.seg >= 2 && !allW && rule.warehouses.indexOf(t[1]) < 0) return false;
        if (rule.seg >= 3 && !allR && rule.routes.indexOf(t.slice(0, 3).join('-')) < 0) return false;
        if (rule.seg >= 4 && !allN && rule.nums.indexOf(c) < 0) return false;
        return true;
    });
};

/** 多条规则合并后的分拣码（按出现顺序去重） */
export const customCodes = (destination: string, rules: CodeRule[], allCodes: string[]): string[] => {
    const out: string[] = [];
    rules.forEach((r) => ruleCodes(destination, r, allCodes).forEach((c) => {
        if (out.indexOf(c) < 0) out.push(c);
    }));
    return out;
};

/** 每码包牌数上限：防止误输入导致合计包牌数失控增长（批量创建接口与打印都要扛住） */
export const MAX_COPIES_PER_CODE = 10;

export const normalizeCopies = (copies: number | string | undefined | null): number => {
    const n = parseInt(String(copies ?? 1), 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, MAX_COPIES_PER_CODE);
};

/** 自定义规则合计包牌数 = Σ 规则命中码数 × 每码包牌数（规则间重复的码分别计数） */
export const customTotal = (destination: string, rules: CodeRule[], allCodes: string[]): number =>
    rules.reduce((sum, r) => sum + ruleCodes(destination, r, allCodes).length * normalizeCopies(r.copies), 0);

/** 规则编辑器：配送仓可选值 */
export const warehouseOptions = (destination: string, allCodes: string[]): string[] =>
    Array.from(new Set(codesAt(destination, 2, allCodes).map((c) => c.split('-')[1]).filter(Boolean)));

/** 规则编辑器：路区可选值（三段码），受配送仓选择约束 */
export const routeOptions = (destination: string, rule: CodeRule, allCodes: string[]): string[] =>
    codesAt(destination, 3, allCodes).filter((c) => isAll(rule.warehouses) || rule.warehouses.indexOf(c.split('-')[1]) >= 0);

/** 规则编辑器：编号可选值（四段码），受配送仓与路区约束；选全部路区时无可选编号 */
export const numOptions = (destination: string, rule: CodeRule, allCodes: string[]): string[] => {
    if (isAll(rule.routes)) return [];
    return codesAt(destination, 4, allCodes).filter((c) => {
        const t = c.split('-');
        return (isAll(rule.warehouses) || rule.warehouses.indexOf(t[1]) >= 0)
            && rule.routes.indexOf(t.slice(0, 3).join('-')) >= 0;
    });
};

/** chip 展示：去掉 Hub 段 */
export const codeTail = (code: string): string => code.split('-').slice(1).join('-');

/**
 * 切换多选 chip：选「全部」→ 只保留全部；在「全部」状态下选具体值 → 只保留该值；
 * 取消到空 → 回到全部。
 */
export const toggleToken = (values: string[], token: string): string[] => {
    if (token === ALL_TOKEN) return [ALL_TOKEN];
    if (isAll(values)) return [token];
    const next = values.indexOf(token) >= 0 ? values.filter((v) => v !== token) : [...values, token];
    return next.length ? next : [ALL_TOKEN];
};

export const isAllSelected = isAll;

export const buildBagPlan = (
    destination: string,
    allCodes: string[],
    options: { custom: boolean; seg?: number; copies?: number; rules?: CodeRule[] },
): BagPlan => {
    const copies = normalizeCopies(options.copies);
    if (options.custom) {
        const rules = options.rules && options.rules.length ? options.rules : [defaultRule(destination)];
        const codes = customCodes(destination, rules, allCodes);
        return { custom: true, codes, copies, total: customTotal(destination, rules, allCodes) };
    }
    const seg = options.seg ?? defaultSegFor(destination);
    const codes = codesAt(destination, seg, allCodes);
    return { custom: false, seg, codes, copies, total: codes.length * copies };
};

/** 按计划展开为「分拣码 × 份数」的包牌码序列（自定义规则按各规则份数展开） */
export const expandPlanCodes = (
    destination: string,
    allCodes: string[],
    options: { custom: boolean; seg?: number; copies?: number; rules?: CodeRule[] },
): string[] => {
    if (options.custom) {
        const rules = options.rules && options.rules.length ? options.rules : [defaultRule(destination)];
        const out: string[] = [];
        rules.forEach((r) => {
            const n = normalizeCopies(r.copies);
            ruleCodes(destination, r, allCodes).forEach((c) => {
                for (let i = 0; i < n; i += 1) out.push(c);
            });
        });
        return out;
    }
    const plan = buildBagPlan(destination, allCodes, options);
    const out: string[] = [];
    plan.codes.forEach((c) => {
        for (let i = 0; i < plan.copies; i += 1) out.push(c);
    });
    return out;
};
